import type { TrendsApiResponse } from "../services/TrendsService";
import type { TrendsPageViewModel } from "./trendsPageMappers";
import { mapTrendsApiToViewModel } from "./trendsPageMappers";
import { readApiDistance } from "./fitnessMetrics";

export interface HealthSyncMetricBlock {
  value?: number;
  goal?: number;
  progress_pct?: number;
}

export interface HealthSyncDailyGraphPoint {
  date: string;
  day: string;
  has_data?: boolean;
  steps: number;
  calories: number;
  sleep_minutes: number;
  distance_km?: number;
  distance?: number;
  active_zone_minutes?: number;
}

export interface HealthSyncVitalMetric {
  value?: number | null;
  unit?: string;
  label?: string;
  has_data?: boolean;
}

/** GET /food_scanner/health-sync/ response */
export interface HealthSyncApiResponse {
  success?: { code?: string; message?: string };
  has_data?: boolean;
  user_id?: string;
  date_local?: string;
  /** Some responses use selected_date / today_local instead of date_local. */
  selected_date?: string;
  today_local?: string;
  last_synced_at?: string;
  records_synced_7d?: number;
  data_source?: string;
  today_overview?: {
    date?: string;
    steps?: HealthSyncMetricBlock;
    calories?: HealthSyncMetricBlock;
    active_zone_minutes?: HealthSyncMetricBlock;
    /** Backend may send km as `distance_km` or `distance`. */
    distance_km?: number;
    distance?: number;
  };
  progress_7d?: {
    start_date?: string;
    end_date?: string;
    label?: string;
    steps?: {
      weekly_total?: number;
      daily_avg?: number;
      goal?: number;
      trend_pct?: number;
      trend_direction?: string;
    };
    calories_burned?: {
      weekly_total?: number;
      daily_avg?: number;
      goal?: number;
      distance?: number;
      distance_km?: number;
    };
    sleep?: {
      last_night_minutes?: number;
      last_night_display?: string;
      goal_minutes?: number;
      goal_display?: string;
      weekly_avg_minutes?: number;
    };
  };
  vitals?: {
    /** Plain number (e.g. 27.04) or `{ value, unit, has_data }` block. */
    hrv_rmssd?: number | HealthSyncVitalMetric | null;
    spo2_percent?: number | HealthSyncVitalMetric | null;
    breathing_rate?: number | HealthSyncVitalMetric | null;
    skin_temp_celsius?: number | HealthSyncVitalMetric | null;
    has_data?: boolean;
  };
  /** Some responses expose vitals as top-level numbers. */
  hrv_rmssd?: number | null;
  spo2_percent?: number | null;
  breathing_rate?: number | null;
  skin_temp_celsius?: number | null;
  outcomes_this_week?: Record<string, unknown>;
  daily_graph?: HealthSyncDailyGraphPoint[];
  /** Top-level distance in meters (health-sync `distance` field). */
  distance?: number;
  distance_m?: number;
  distance_km?: number;
}

/** Accept plain numbers (`27.04`) or `{ value: 27.04 }` metric blocks. */
const vitalValue = (
  metric?: number | string | HealthSyncVitalMetric | null,
): number | undefined => {
  if (metric == null || metric === "") return undefined;
  if (typeof metric === "number") {
    return Number.isFinite(metric) ? metric : undefined;
  }
  if (typeof metric === "string") {
    const n = Number(metric);
    return Number.isFinite(n) ? n : undefined;
  }
  if (metric.value == null) return undefined;
  const n = Number(metric.value);
  return Number.isFinite(n) ? n : undefined;
};

const readHrvRmssd = (health: HealthSyncApiResponse): number | undefined =>
  vitalValue(health.vitals?.hrv_rmssd) ?? vitalValue(health.hrv_rmssd);

const formatDailyGraphLabel = (date: string, fallbackDay: string): string => {
  const trimmed = fallbackDay.trim();
  if (trimmed) return trimmed.length <= 3 ? trimmed : trimmed.slice(0, 3);
  const d = new Date(`${date}T12:00:00`);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 3);
  }
  return "";
};

const mapDailyGraph = (
  graph: HealthSyncDailyGraphPoint[],
  pickValue: (row: HealthSyncDailyGraphPoint) => number,
) =>
  graph.map((d) => ({
    day: formatDailyGraphLabel(d.date, d.day),
    value: pickValue(d),
  }));

/** Convert health-sync API shape → trends mapper input */
export const normalizeHealthSyncToTrendsApi = (
  health: HealthSyncApiResponse | null | undefined,
): TrendsApiResponse | null => {
  if (!health || typeof health !== "object") return null;
  const hasVitals =
    readHrvRmssd(health) != null ||
    vitalValue(health.vitals?.spo2_percent) != null ||
    vitalValue(health.spo2_percent) != null;
  if (!health.has_data && !health.daily_graph?.length && !hasVitals) return null;

  const overview = health.today_overview;
  const progress = health.progress_7d;
  const vitals = health.vitals;
  const graph = health.daily_graph ?? [];
  const selectedDate =
    health.date_local ?? health.selected_date ?? health.today_local ?? overview?.date;
  const dayRow = selectedDate
    ? graph.find((row) => row.date === selectedDate)
    : undefined;
  const distanceM = readApiDistance(
    overview as Record<string, unknown> | undefined,
    progress?.calories_burned as Record<string, unknown> | undefined,
    dayRow as Record<string, unknown> | undefined,
    health as unknown as Record<string, unknown>,
  );

  const stepsGraph = mapDailyGraph(graph, (d) => d.steps);
  const calGraph = mapDailyGraph(graph, (d) => d.calories);
  const sleepGraph = mapDailyGraph(graph, (d) =>
    d.sleep_minutes > 0 ? d.sleep_minutes / 60 : 0,
  );

  const sleepMinutes = progress?.sleep?.last_night_minutes ?? 0;
  const sleepGoalHours = progress?.sleep?.goal_minutes
    ? Math.round(progress.sleep.goal_minutes / 60)
    : 8;

  return {
    has_data: health.has_data || hasVitals || Boolean(health.daily_graph?.length),
    date_local: health.date_local ?? health.selected_date ?? health.today_local,
    today_date_local: health.today_local,
    today_overview: {
      steps: overview?.steps?.value,
      steps_goal: overview?.steps?.goal,
      calories_burned: overview?.calories?.value,
      calories_goal: overview?.calories?.goal,
      active_zone_minutes: overview?.active_zone_minutes?.value,
      active_zone_minutes_goal: overview?.active_zone_minutes?.goal,
      distance_m: distanceM,
      sleep_minutes: sleepMinutes,
      sleep_hours: progress?.sleep?.last_night_display,
      sleep_goal: sleepGoalHours,
      hrv_rmssd: readHrvRmssd(health),
      spo2_percent: vitalValue(vitals?.spo2_percent) ?? vitalValue(health.spo2_percent),
      breathing_rate:
        vitalValue(vitals?.breathing_rate) ?? vitalValue(health.breathing_rate),
      skin_temp_celsius:
        vitalValue(vitals?.skin_temp_celsius) ?? vitalValue(health.skin_temp_celsius),
    },
    progress: {
      weekly_steps: {
        weekly_total: progress?.steps?.weekly_total,
        daily_avg: progress?.steps?.daily_avg,
        goal: progress?.steps?.goal,
        graph: stepsGraph,
      },
      weekly_calories_burned: {
        daily_avg: progress?.calories_burned?.daily_avg,
        goal: progress?.calories_burned?.goal,
        graph: calGraph,
      },
      weekly_sleep: {
        graph: sleepGraph,
      },
    },
    learning: {
      hrv: {
        current: readHrvRmssd(health),
      },
      vitals: {
        spo2_percent: vitalValue(vitals?.spo2_percent) ?? vitalValue(health.spo2_percent),
        breathing_rate:
          vitalValue(vitals?.breathing_rate) ?? vitalValue(health.breathing_rate),
        skin_temp_celsius:
          vitalValue(vitals?.skin_temp_celsius) ?? vitalValue(health.skin_temp_celsius),
      },
    },
    distance_m: distanceM,
    hrv_rmssd: readHrvRmssd(health),
    spo2_percent: vitalValue(vitals?.spo2_percent) ?? vitalValue(health.spo2_percent),
    breathing_rate:
      vitalValue(vitals?.breathing_rate) ?? vitalValue(health.breathing_rate),
    skin_temp_celsius:
      vitalValue(vitals?.skin_temp_celsius) ?? vitalValue(health.skin_temp_celsius),
  };
};

export const mapHealthSyncApiToViewModel = (
  health: HealthSyncApiResponse | null | undefined,
): TrendsPageViewModel | null => {
  const normalized = normalizeHealthSyncToTrendsApi(health);
  if (!normalized) return null;
  return mapTrendsApiToViewModel(normalized);
};

const mergeFitnessDaily = (
  base: TrendsPageViewModel["daily"],
  fitness: TrendsPageViewModel["daily"],
): TrendsPageViewModel["daily"] => ({
  ...base,
  steps: fitness.steps,
  steps_goal: fitness.steps_goal,
  calories_burned: fitness.calories_burned,
  calories_goal: fitness.calories_goal,
  active_zone_minutes: fitness.active_zone_minutes,
  active_zone_minutes_goal: fitness.active_zone_minutes_goal,
  sleep_minutes: fitness.sleep_minutes,
  sleep_hours: fitness.sleep_hours,
  sleep_goal: fitness.sleep_goal,
  distance_m: fitness.distance_m ?? base.distance_m,
  hrv_rmssd: fitness.hrv_rmssd ?? base.hrv_rmssd,
  spo2_percent: fitness.spo2_percent ?? base.spo2_percent,
  breathing_rate: fitness.breathing_rate ?? base.breathing_rate,
  skin_temp_celsius: fitness.skin_temp_celsius ?? base.skin_temp_celsius,
});

/** Overlay Google Health fitness fields from health-sync onto glucose/BBS/weight trends. */
export const mergeFitnessTrendsViewModel = (
  base: TrendsPageViewModel,
  fitness: TrendsPageViewModel | null | undefined,
): TrendsPageViewModel => {
  if (!fitness?.hasData) return base;

  return {
    ...base,
    hasData: base.hasData || fitness.hasData,
    dateLabel: fitness.dateLabel || base.dateLabel,
    daily: mergeFitnessDaily(base.daily, fitness.daily),
    stepsBarData: fitness.stepsBarData,
    calBarData: fitness.calBarData,
    sleepBarData: fitness.sleepBarData,
    hrvLineData: fitness.hrvLineData,
    weeklyStepAvg: fitness.weeklyStepAvg,
    weeklyStepTotal: fitness.weeklyStepTotal,
    weeklyCalAvg: fitness.weeklyCalAvg,
  };
};
